import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Lock,
  LockOpen,
  VpnKey,
  Visibility,
  VisibilityOff,
  Person,
  SupervisorAccount,
  AdminPanelSettings,
} from '@mui/icons-material';
import { staffAPI } from '../services/api';

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 8 && !digits.startsWith('0')) return `${digits.slice(0,4)}-${digits.slice(4)}`;
  if (digits.startsWith('02')) {
    if (digits.length <= 5) return `${digits.slice(0,2)}-${digits.slice(2)}`;
    if (digits.length <= 9) return `${digits.slice(0,2)}-${digits.slice(2,5)}-${digits.slice(5)}`;
    return `${digits.slice(0,2)}-${digits.slice(2,6)}-${digits.slice(6,10)}`;
  }
  if (digits.length === 11) return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return digits.slice(0, 11);
};

function StaffList() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // 등록/수정 다이얼로그
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    phone: '',
    role: 'operator',
  });
  const [showPassword, setShowPassword] = useState(false);

  // 비밀번호 변경 다이얼로그
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // 삭제 확인 다이얼로그
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // 통계
  const [stats, setStats] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadStaff();
    loadStats();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await staffAPI.getAll();
      setStaff(response.data.data);
    } catch (err) {
      setError('직원 목록을 불러오는데 실패했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await staffAPI.getStats();
      setStats(response.data.data);
    } catch (err) {
      console.error('통계 로드 실패:', err);
    }
  };

  const getRoleText = (role) => {
    const roleMap = {
      super_admin: '최고관리자',
      admin: '최고관리자',
      manager: '관리자',
      operator: '직원',
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      super_admin: 'error',
      admin: 'error',
      manager: 'warning',
      operator: 'info',
    };
    return colorMap[role] || 'default';
  };

  const getRoleIcon = (role) => {
    const iconMap = {
      super_admin: <AdminPanelSettings fontSize="small" />,
      admin: <AdminPanelSettings fontSize="small" />,
      manager: <SupervisorAccount fontSize="small" />,
      operator: <Person fontSize="small" />,
    };
    return iconMap[role] || <Person fontSize="small" />;
  };

  const handleAddClick = () => {
    setEditMode(false);
    setFormData({
      username: '',
      password: '',
      name: '',
      email: '',
      phone: '',
      role: 'operator',
    });
    setDialogOpen(true);
  };

  const handleEditClick = (staffMember) => {
    setEditMode(true);
    setFormData({
      admin_id: staffMember.admin_id,
      username: staffMember.username,
      name: staffMember.name,
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      // DB의 super_admin / admin 모두 화면 라벨은 '최고관리자' 동일 — Select MenuItem 에 맞게 admin 으로 매핑
      role: staffMember.role === 'super_admin' ? 'admin' : staffMember.role,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        alert('이름은 필수입니다.');
        return;
      }

      if (editMode) {
        await staffAPI.update(formData.admin_id, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
        });
        alert('직원 정보가 수정되었습니다.');
      } else {
        if (!formData.username || !formData.password) {
          alert('아이디와 비밀번호는 필수입니다.');
          return;
        }
        await staffAPI.create(formData);
        alert('직원이 등록되었습니다.');
      }

      setDialogOpen(false);
      loadStaff();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.message || '저장에 실패했습니다.');
      console.error(err);
    }
  };

  const handleStatusChange = async (staffMember, newStatus) => {
    try {
      await staffAPI.updateStatus(staffMember.admin_id, { is_active: newStatus });
      alert(newStatus ? '계정이 활성화되었습니다.' : '계정이 비활성화되었습니다.');
      loadStaff();
    } catch (err) {
      alert(err.response?.data?.message || '상태 변경에 실패했습니다.');
      console.error(err);
    }
  };

  const handlePasswordClick = (staffMember) => {
    setPasswordTarget(staffMember);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordChange = async () => {
    try {
      if (!newPassword || newPassword.length < 4) {
        alert('비밀번호는 4자 이상이어야 합니다.');
        return;
      }
      await staffAPI.changePassword(passwordTarget.admin_id, { newPassword });
      alert('비밀번호가 변경되었습니다.');
      setPasswordDialogOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
      console.error(err);
    }
  };

  const handleDeleteClick = (staffMember) => {
    setDeleteTarget(staffMember);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await staffAPI.delete(deleteTarget.admin_id);
      alert('직원이 삭제되었습니다.');
      setDeleteDialogOpen(false);
      loadStaff();
      loadStats();
    } catch (err) {
      alert(err.response?.data?.message || '삭제에 실패했습니다.');
      console.error(err);
    }
  };

  const isSuperAdmin = currentUser.role === 'super_admin' || currentUser.role === 'admin';
  const visibleStaff = isSuperAdmin
    ? staff
    : staff.filter((s) => s.role !== 'super_admin' && s.role !== 'admin');

  const filteredStaff = visibleStaff.filter((s) => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return s.role === 'super_admin' || s.role === 'admin' || s.role === 'manager';
    if (tabValue === 2) return s.role === 'operator';
    return true;
  });

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 3, px: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          직원 관리
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Add />}
            onClick={handleAddClick}
          >
            직원 등록
          </Button>
          <IconButton onClick={loadStaff} color="primary" size="large">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* 통계 카드 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                전체 직원
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {visibleStaff.length}명
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                관리자
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {visibleStaff.filter((s) => s.role === 'super_admin' || s.role === 'admin' || s.role === 'manager').length}명
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                활성 계정
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {visibleStaff.filter((s) => s.is_active).length}명
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ffebee' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                비활성 계정
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {visibleStaff.filter((s) => !s.is_active).length}명
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 탭 필터 */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`전체 (${visibleStaff.length})`} />
          <Tab label={`관리자 (${visibleStaff.filter((s) => s.role === 'super_admin' || s.role === 'admin' || s.role === 'manager').length})`} />
          <Tab label={`직원 (${visibleStaff.filter((s) => s.role === 'operator').length})`} />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>아이디</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>이름</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>역할</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>연락처</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>이메일</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>상태</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>주문생성</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>배차처리</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>마지막 로그인</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#1976d2', color: 'white' }}>작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStaff.map((staffMember) => (
                <TableRow
                  key={staffMember.admin_id}
                  hover
                  sx={{
                    bgcolor: !staffMember.is_active ? '#f5f5f5' : 'inherit',
                    opacity: !staffMember.is_active ? 0.7 : 1,
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>
                    {staffMember.username}
                    {staffMember.admin_id === currentUser.admin_id && (
                      <Chip label="나" size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{staffMember.name}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getRoleIcon(staffMember.role)}
                      label={getRoleText(staffMember.role)}
                      color={getRoleColor(staffMember.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{staffMember.phone ? formatPhone(staffMember.phone) : '-'}</TableCell>
                  <TableCell>{staffMember.email || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={staffMember.is_active ? '활성' : '비활성'}
                      color={staffMember.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{staffMember.orders_created || 0}건</TableCell>
                  <TableCell align="center">{staffMember.orders_assigned || 0}건</TableCell>
                  <TableCell>
                    {staffMember.last_login
                      ? new Date(staffMember.last_login).toLocaleString('ko-KR')
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="수정">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(staffMember)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="비밀번호 변경">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handlePasswordClick(staffMember)}
                        >
                          <VpnKey fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {staffMember.username !== 'admin' && staffMember.admin_id !== currentUser.admin_id && (
                        <>
                          <Tooltip title={staffMember.is_active ? '비활성화' : '활성화'}>
                            <IconButton
                              size="small"
                              color={staffMember.is_active ? 'warning' : 'success'}
                              onClick={() => handleStatusChange(staffMember, !staffMember.is_active)}
                            >
                              {staffMember.is_active ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="삭제">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(staffMember)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 등록/수정 다이얼로그 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editMode ? '직원 정보 수정' : '직원 등록'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="아이디"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              disabled={editMode}
              required
              fullWidth
            />
            {!editMode && (
              <TextField
                label="비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <TextField
              label="이름"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              inputProps={{ lang: 'ko' }}
            />
            <TextField
              label="연락처"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
              placeholder="010-0000-0000"
              fullWidth
            />
            <TextField
              label="이메일"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              inputProps={{ lang: 'en', inputMode: 'email' }}
            />
            <TextField
              select
              label="역할"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              fullWidth
            >
              <MenuItem value="operator">직원</MenuItem>
              <MenuItem value="manager">관리자</MenuItem>
              {isSuperAdmin && <MenuItem value="admin">최고관리자</MenuItem>}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editMode ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>비밀번호 변경</DialogTitle>
        <DialogContent>
          {passwordTarget && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {passwordTarget.name} ({passwordTarget.username})
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                비밀번호는 영문 대·소문자, 숫자, 특수문자(!,#,@,%,/)를 모두 포함해야 합니다.
              </Alert>
              <TextField
                label="새 비밀번호"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>취소</Button>
          <Button onClick={handlePasswordChange} variant="contained" color="primary">
            변경
          </Button>
        </DialogActions>
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>직원 삭제</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Box>
              <Typography>정말로 이 직원을 삭제하시겠습니까?</Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography>
                  {deleteTarget.name} ({deleteTarget.username})
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  역할: {getRoleText(deleteTarget.role)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default StaffList;
